import React, { useState, useEffect } from 'react';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { 
  FileQuestion, CheckCircle, XCircle, Clock, Loader2, 
  ExternalLink, Building2, Merge
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPublisherRequests = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [approveModal, setApproveModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [mergeModal, setMergeModal] = useState(null);

  useEffect(() => {
    fetchRequests();
    fetchPublishers();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const statusParam = filter === 'all' ? '' : filter;
      const res = await fetch(`${API_URL}/api/admin/publisher-requests?status=${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublishers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPublishers(data.publishers || []);
      }
    } catch (err) {
      console.error('Error fetching publishers:', err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="flex items-center gap-1 text-green-400 bg-green-950/50 px-2 py-1 rounded text-xs">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 text-red-400 bg-red-950/50 px-2 py-1 rounded text-xs">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      case 'merged':
        return (
          <span className="flex items-center gap-1 text-blue-400 bg-blue-950/50 px-2 py-1 rounded text-xs">
            <Merge className="w-3 h-3" />
            Merged
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-yellow-400 bg-yellow-950/50 px-2 py-1 rounded text-xs">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 p-6" data-testid="admin-requests-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileQuestion className="w-6 h-6 text-purple-500" />
            Publisher Requests
          </h1>
          <p className="text-gray-400 text-sm">Review requests from creators to add new publishers</p>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-gray-900 border-gray-800">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="merged">Merged</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 bg-gray-900 rounded-lg border border-gray-800">
            <FileQuestion className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">No requests found</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="requests-list">
            {requests.map((req) => (
              <div
                key={req.id}
                className="p-4 bg-gray-900 border border-gray-800 rounded-lg"
                data-testid={`request-${req.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  {/* Request Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-medium text-white">{req.name}</h3>
                      {getStatusBadge(req.status)}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-400 mb-3">
                      <div>
                        <span className="text-gray-500">Requested by:</span> {req.requester_email}
                      </div>
                      {req.website_url && (
                        <div>
                          <span className="text-gray-500">Website:</span>{' '}
                          <a 
                            href={req.website_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-purple-400 hover:underline inline-flex items-center gap-1"
                          >
                            {req.website_url.slice(0, 40)}...
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                      {req.contact_email && (
                        <div>
                          <span className="text-gray-500">Contact:</span> {req.contact_email}
                        </div>
                      )}
                      {req.proof_url && (
                        <div>
                          <span className="text-gray-500">Proof:</span>{' '}
                          <a 
                            href={req.proof_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-purple-400 hover:underline inline-flex items-center gap-1"
                          >
                            View Proof
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>

                    {req.additional_info && (
                      <div className="text-sm text-gray-400 bg-gray-800/50 p-2 rounded mb-3">
                        {req.additional_info}
                      </div>
                    )}

                    <div className="text-xs text-gray-500">
                      Submitted: {new Date(req.created_at).toLocaleString()}
                      {req.reviewed_at && (
                        <span className="ml-4">
                          Reviewed: {new Date(req.reviewed_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {req.status === 'pending' && (
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => setApproveModal(req)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-btn-${req.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve & Create
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setMergeModal(req)}
                        className="border-blue-600 text-blue-400 hover:bg-blue-950"
                      >
                        <Merge className="w-4 h-4 mr-1" />
                        Merge Existing
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setRejectModal(req)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>

                {/* Admin Notes (for rejected) */}
                {req.admin_notes && req.status === 'rejected' && (
                  <div className="mt-3 pt-3 border-t border-gray-800 text-sm text-red-400">
                    <strong>Reason:</strong> {req.admin_notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Approve Modal */}
        <ApproveModal
          request={approveModal}
          onClose={() => setApproveModal(null)}
          onSuccess={() => {
            setApproveModal(null);
            fetchRequests();
            fetchPublishers();
          }}
        />

        {/* Reject Modal */}
        <RejectModal
          request={rejectModal}
          onClose={() => setRejectModal(null)}
          onSuccess={() => {
            setRejectModal(null);
            fetchRequests();
          }}
        />

        {/* Merge Modal */}
        <MergeModal
          request={mergeModal}
          publishers={publishers}
          onClose={() => setMergeModal(null)}
          onSuccess={() => {
            setMergeModal(null);
            fetchRequests();
          }}
        />
      </div>
    </div>
  );
};

// Approve Modal - Creates a new publisher
const ApproveModal = ({ request, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    website_url: '',
    verified: false
  });

  useEffect(() => {
    if (request) {
      setFormData({
        name: request.name,
        slug: request.name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').slice(0, 50),
        description: '',
        website_url: request.website_url || '',
        verified: false
      });
    }
  }, [request]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/publisher-requests/${request.id}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to approve');
      }

      toast.success('Publisher created');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={!!request} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Approve & Create Publisher</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-gray-800 border-gray-700"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Slug</label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="bg-gray-800 border-gray-700"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-gray-800 border-gray-700"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Website URL</label>
            <Input
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              className="bg-gray-800 border-gray-700"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="verified-approve"
              checked={formData.verified}
              onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
              className="rounded border-gray-700 bg-gray-800"
            />
            <label htmlFor="verified-approve" className="text-sm text-gray-300">
              Mark as Verified
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Publisher'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Reject Modal
const RejectModal = ({ request, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/publisher-requests/${request.id}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to reject');
      }

      toast.success('Request rejected');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={!!request} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white">
        <DialogHeader>
          <DialogTitle>Reject Request</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-gray-400">
            Rejecting request for <strong className="text-white">{request.name}</strong>
          </p>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Reason *</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this request is being rejected..."
              className="bg-gray-800 border-gray-700 min-h-[100px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Merge Modal
const MergeModal = ({ request, publishers, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPublisherId, setSelectedPublisherId] = useState('');

  const handleSubmit = async () => {
    if (!selectedPublisherId) {
      toast.error('Please select a publisher');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/publisher-requests/${request.id}/merge`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ publisher_id: selectedPublisherId })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to merge');
      }

      toast.success('Request merged with existing publisher');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={!!request} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white">
        <DialogHeader>
          <DialogTitle>Merge with Existing Publisher</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-gray-400">
            Merge request for <strong className="text-white">{request.name}</strong> with an existing publisher.
          </p>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Select Publisher</label>
            <Select value={selectedPublisherId} onValueChange={setSelectedPublisherId}>
              <SelectTrigger className="bg-gray-800 border-gray-700">
                <SelectValue placeholder="Select a publisher..." />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800 max-h-60">
                {publishers.map((pub) => (
                  <SelectItem key={pub.id} value={pub.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      {pub.name}
                      {pub.verified && <CheckCircle className="w-3 h-3 text-purple-500" />}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Merge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPublisherRequests;
